import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Utility function to clean up lingering scrollbars and reset scroll state
 * This is particularly useful when navigating away from pages with PDF viewers
 * (Apryse / WebViewer can leave document-level overflow styles behind that
 * trap the host page in a stuck-scroll state).
 *
 * IMPORTANT — opt-out contract: the final DOM sweep below targets
 * `div[style*="overflow"]` and strips the inline `overflow` from every
 * matching div. Any scroll container that should NOT be wiped — for
 * example, a sticky right-rail panel that owns its own scroll axis and
 * needs to survive route changes — must carry the `intentional-scroll`
 * class. Without that opt-out the cleanup pass will silently freeze
 * the panel on the next pathname change because the inline-style
 * removal wins over any styled-components CSS rule.
 *
 * Known consumers of `intentional-scroll`:
 *   - `src/components/AIPane/AIPaneStyled.js` — `AIPaneScrollBody`,
 *     `AnalysisProgressContainer`, `AnalysisReportContainer`,
 *     `InboxListBody` (Brief / Context Analysis / Tasks / Messages
 *     panel bodies, all sit inside the right-rail AI pane that
 *     stays mounted across matter-internal navigation).
 *   - `src/components/AIPane/AnalysisReport.js` — the inline-styled
 *     scroll container that wraps the rendered report.
 *
 * If you add a new persistent scroll container that lives across
 * route changes, mark it `intentional-scroll` too.
 */
export const cleanupScrollState = () => {
  // Reset scroll position
  window.scrollTo(0, 0);
  
  // Reset body and document element overflow
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  
  // Remove any WebViewer-related scrollbars that might be lingering
  const webviewerElements = document.querySelectorAll('.webviewer');
    for (const element of webviewerElements){
    element.style.overflow = '';
    element.innerHTML = '';
  };
  
  // Remove any PDFTron-related elements that might be lingering
  const pdftronElements = document.querySelectorAll('[data-element-id]');
    for (const element of pdftronElements) {
    element.style?.overflow && (element.style.overflow = '');
  };
  
  // Remove any iframe elements that might have scrollbars
  const iframeElements = document.querySelectorAll('iframe');
  for (const iframe of iframeElements) {
    iframe.style?.overflow && (iframe.style.overflow = '');
  };
  
  // Remove any div elements with scrollbars that might be from PDF viewers
  const scrollableDivs = document.querySelectorAll('div[style*="overflow"]');
  for (const div of scrollableDivs) {
    const style = div.getAttribute('style');
    if (style?.includes('overflow')) {
      // Only reset overflow if it's not intentionally set for layout purposes
      if (!div.classList.contains('intentional-scroll') && 
          !div.id?.includes('layout') && 
          !div.className?.includes('layout')) {
        div.style.overflow = '';
      }
    }
  };
};

/**
 * Hook to automatically clean up scroll state when component unmounts
 * Usage: useScrollCleanup() in useEffect cleanup
 */
export const useScrollCleanup = () => {
  return () => {
    cleanupScrollState();
  };
};

/**
 * Hook to automatically clean up scroll state on route changes
 * Usage: useRouteScrollCleanup() in a component that needs to reset scroll on navigation
 */
export const useRouteScrollCleanup = () => {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    // Clean up scroll state when route changes
    cleanupScrollState();
  }, [pathname]);
};
